/**
 * Wall-clock entry/parse helpers for the time/datetime picker widgets.
 *
 * These are the widget-internal helpers permitted by the picker exception in
 * `.cursor/rules/frontend-datetime-agent.mdc` — they handle the typed-string ↔ canonical
 * `HH:mm` cycle and 12h/24h presentation for the entry controls. They never convert
 * timezones; all timezone/display work for business timestamps stays in the canonical
 * `@/shared/lib/tenantDateTime` module.
 */

const TENANT_TIME_FORMAT_12 = 12
const TENANT_TIME_FORMAT_24 = 24

export type TenantTimeFormat = typeof TENANT_TIME_FORMAT_12 | typeof TENANT_TIME_FORMAT_24

export type TenantTimeFormatInput = number | string | null | undefined

/**
 * Coerces tenant time-format values from JSON / props to a strict 12 | 24 contract.
 * Unknown values fall back to 24 (DB / legacy default).
 */
export const normalizeTenantTimeFormat = (value: TenantTimeFormatInput): TenantTimeFormat => {
  // Number('12') === 12; Number('12abc') === NaN → 24 (unlike parseInt, which would accept '12abc')
  const numericValue = typeof value === 'string' ? Number(value.trim()) : Number(value)
  if (numericValue === TENANT_TIME_FORMAT_12) return TENANT_TIME_FORMAT_12
  return TENANT_TIME_FORMAT_24
}

export const usesTwelveHourClock = (tenantTimeFormat: TenantTimeFormatInput): boolean =>
  normalizeTenantTimeFormat(tenantTimeFormat) === TENANT_TIME_FORMAT_12

export type DayPeriod = 'AM' | 'PM'

export interface WallClockParts {
  hour: number
  minute: number
}

export interface TwelveHourClockParts {
  hour: number
  minute: number
  period: DayPeriod
}

/**
 * Parses a wall-clock string (`HH:mm`, `HH:mm:ss`, or unpadded `H:mm`) into 0–23 hour parts.
 * Returns null for malformed or out-of-range values.
 */
export const parseWallClockParts = (wallClockTime: string): WallClockParts | null => {
  const match = wallClockTime.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
  if (!match) return null

  const hour = Number(match[1])
  const minute = Number(match[2])
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour > 23 || minute > 59) {
    return null
  }

  return { hour, minute }
}

/** Canonical storage / submit form: zero-padded 24h `HH:mm`. */
export const toCanonicalWallClock = (hour: number, minute: number): string =>
  `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`

export const toTwelveHourClockParts = (hour: number, minute: number): TwelveHourClockParts => ({
  hour: hour === 0 ? 12 : hour > 12 ? hour - 12 : hour,
  minute,
  period: hour >= 12 ? 'PM' : 'AM',
})

export const fromTwelveHourClockParts = ({
  hour,
  minute,
  period,
}: TwelveHourClockParts): WallClockParts => {
  const hourTwelve = ((hour - 1) % 12) + 1
  const hourTwentyFour = (hourTwelve % 12) + (period === 'PM' ? 12 : 0)
  return { hour: hourTwentyFour, minute }
}

/**
 * Presentation-only hour:minute formatting matching legacy timeFormat pipe
 * (`hh:mm A` vs zero-padded `HH:mm`). Does not invent a timezone.
 */
export const formatHourMinute = (
  hour: number,
  minute: number,
  tenantTimeFormat: TenantTimeFormatInput,
): string => {
  const formattedMinute = minute.toString().padStart(2, '0')

  if (usesTwelveHourClock(tenantTimeFormat)) {
    const { hour: displayHour, period } = toTwelveHourClockParts(hour, minute)
    return `${displayHour}:${formattedMinute} ${period}`
  }

  return `${hour.toString().padStart(2, '0')}:${formattedMinute}`
}

export interface WallClockEntryPresentation {
  mode: TenantTimeFormat
  hourText: string
  minuteText: string
  period: DayPeriod | null
  label: string
  showsMeridian: boolean
}

/**
 * Describes how a Starts At / wall-clock entry control should present a canonical `HH:mm` value
 * under the tenant's 12/24 setting. Used by TenantTimeInput and FE unit tests (AC: midnight ≠ AM/PM in 24h).
 * Empty string stays empty in the hour/minute fields (no silent midnight presentation).
 */
export const wallClockEntryPresentation = (
  wallClockTime: string,
  tenantTimeFormat: TenantTimeFormatInput,
): WallClockEntryPresentation => {
  const mode = normalizeTenantTimeFormat(tenantTimeFormat)

  if (wallClockTime.trim() === '') {
    return {
      mode,
      hourText: '',
      minuteText: '',
      // Keep a safe period for the 12h select without inventing a displayed noon/midnight time.
      period: mode === TENANT_TIME_FORMAT_12 ? 'AM' : null,
      label: '',
      showsMeridian: mode === TENANT_TIME_FORMAT_12,
    }
  }

  const parts = parseWallClockParts(wallClockTime) ?? { hour: 0, minute: 0 }
  const label = formatHourMinute(parts.hour, parts.minute, mode)

  if (mode === TENANT_TIME_FORMAT_12) {
    const twelveHourParts = toTwelveHourClockParts(parts.hour, parts.minute)
    return {
      mode,
      hourText: String(twelveHourParts.hour),
      minuteText: twelveHourParts.minute.toString().padStart(2, '0'),
      period: twelveHourParts.period,
      label,
      showsMeridian: true,
    }
  }

  return {
    mode,
    hourText: parts.hour.toString().padStart(2, '0'),
    minuteText: parts.minute.toString().padStart(2, '0'),
    period: null,
    label,
    showsMeridian: false,
  }
}

/**
 * Formats a wall-clock time string (Starts At / schedule times) without going through `Date`.
 * Accepts `HH:mm`, `HH:mm:ss`, or unpadded `H:mm`. Unknown input is returned unchanged.
 */
export const formatWallClockTime = (
  wallClockTime: string,
  tenantTimeFormat: TenantTimeFormatInput,
): string => {
  const parts = parseWallClockParts(wallClockTime)
  if (!parts) return wallClockTime

  return formatHourMinute(parts.hour, parts.minute, tenantTimeFormat)
}
